import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
    Loader2,
    LogOut,
    Download,
    Inbox,
    Phone,
    Mail,
    Calculator as CalculatorIcon,
    MessageSquare,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "kp_admin_token";

const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[15px] text-slate-800 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10";

const fmtDate = (iso) =>
    new Date(iso).toLocaleString("es-ES", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

function csvEscape(v) {
    const s = String(v ?? "");
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function AdminPage() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [leads, setLeads] = useState(null);

    const loadLeads = useCallback(async (t) => {
        try {
            const { data } = await axios.get(`${API}/leads`, {
                headers: { Authorization: `Bearer ${t}` },
            });
            setLeads(data);
        } catch (e) {
            if (e.response?.status === 401) {
                localStorage.removeItem(TOKEN_KEY);
                setToken(null);
                setLeads(null);
            }
        }
    }, []);

    useEffect(() => {
        if (token) loadLeads(token);
    }, [token, loadLeads]);

    const login = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.post(`${API}/auth/login`, {
                email: email.trim(),
                password,
            });
            localStorage.setItem(TOKEN_KEY, data.token);
            setToken(data.token);
        } catch (err) {
            const d = err.response?.data?.detail;
            setError(typeof d === "string" ? d : "No se pudo iniciar sesión.");
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setLeads(null);
    };

    const exportCsv = () => {
        const headers = [
            "Fecha",
            "Nombre",
            "Email",
            "Teléfono",
            "Importe estimado",
            "Tipo de hipoteca",
            "Mensaje",
            "Simulación",
        ];
        const rows = leads.map((l) => [
            fmtDate(l.created_at),
            l.name,
            l.email,
            l.phone,
            l.amount || "",
            l.mortgage_type || "",
            l.message || "",
            l.simulation || "",
        ]);
        const csv = "\ufeff" + [headers, ...rows]
            .map((r) => r.map(csvEscape).join(";"))
            .join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `leads-keypoteca-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-[#112338] noise-overlay relative flex items-center justify-center px-4">
                <div className="absolute inset-0 blueprint-grid" />
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="relative w-full max-w-md rounded-3xl bg-white p-8 sm:p-10 shadow-2xl"
                >
                    <img
                        src="/assets/logo-color-trim.png"
                        alt="Keypoteca"
                        className="h-10 w-auto"
                    />
                    <h1 className="mt-6 font-display text-2xl font-bold text-[#0F172A]">
                        Panel de leads
                    </h1>
                    <p className="mt-2 text-sm text-slate-500">
                        Acceso privado para el equipo de Keypoteca.
                    </p>
                    <form
                        data-testid="admin-login-form"
                        onSubmit={login}
                        className="mt-7 grid gap-4"
                    >
                        <input
                            data-testid="admin-email-input"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email"
                            className={inputCls}
                        />
                        <input
                            data-testid="admin-password-input"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Contraseña"
                            className={inputCls}
                        />
                        {error && (
                            <p
                                data-testid="admin-login-error"
                                className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5"
                            >
                                {error}
                            </p>
                        )}
                        <button
                            data-testid="admin-login-submit"
                            type="submit"
                            disabled={loading}
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-[#0B1A2B] font-bold px-6 py-3.5 transition-colors"
                        >
                            {loading ? (
                                <Loader2 size={18} className="animate-spin" />
                            ) : (
                                "Entrar"
                            )}
                        </button>
                    </form>
                </motion.div>
            </div>
        );
    }

    return (
        <div data-testid="admin-panel" className="min-h-screen bg-[#F5F7FA]">
            <header className="bg-[#112338] noise-overlay relative border-b border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <img
                            src="/assets/logo-white-trim.png"
                            alt="Keypoteca"
                            className="h-8 w-auto"
                        />
                        <span className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">
                            Panel de leads
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            data-testid="admin-export-csv"
                            onClick={exportCsv}
                            disabled={!leads || leads.length === 0}
                            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-[#0B1A2B] text-sm font-bold px-5 py-2.5 transition-colors"
                        >
                            <Download size={15} /> Exportar CSV
                        </button>
                        <button
                            data-testid="admin-logout"
                            onClick={logout}
                            className="inline-flex items-center gap-2 rounded-full border border-white/25 text-white text-sm font-semibold px-5 py-2.5 hover:bg-white/5 transition-colors"
                        >
                            <LogOut size={15} /> Salir
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div className="flex items-baseline justify-between">
                    <h1 className="font-display text-2xl font-bold text-[#0F172A]">
                        Solicitudes recibidas
                    </h1>
                    <span
                        data-testid="admin-leads-count"
                        className="font-mono text-sm text-slate-500"
                    >
                        {leads ? `${leads.length} leads` : "..."}
                    </span>
                </div>

                {!leads ? (
                    <div className="mt-16 flex justify-center">
                        <Loader2 size={28} className="animate-spin text-slate-400" />
                    </div>
                ) : leads.length === 0 ? (
                    <div
                        data-testid="admin-empty"
                        className="mt-10 rounded-3xl border border-slate-200 bg-white p-14 text-center"
                    >
                        <Inbox size={36} className="mx-auto text-slate-300" />
                        <p className="mt-4 text-slate-500">
                            Aún no hay solicitudes. Cuando llegue un lead,
                            aparecerá aquí.
                        </p>
                    </div>
                ) : (
                    <div className="mt-8 grid gap-5">
                        {leads.map((l, i) => (
                            <motion.article
                                key={l.id}
                                data-testid={`admin-lead-${i}`}
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: i * 0.04 }}
                                className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-7"
                            >
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <h2 className="font-display font-bold text-lg text-[#0F172A]">
                                            {l.name}
                                        </h2>
                                        <p className="text-xs text-slate-400 mt-0.5">
                                            {fmtDate(l.created_at)}
                                        </p>
                                    </div>
                                    {l.mortgage_type && (
                                        <span className="rounded-full bg-[#1F3B5B]/5 text-[#1F3B5B] text-xs font-semibold px-3.5 py-1.5">
                                            {l.mortgage_type}
                                        </span>
                                    )}
                                </div>

                                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
                                    <a
                                        href={`mailto:${l.email}`}
                                        className="inline-flex items-center gap-2 hover:text-emerald-600 transition-colors"
                                    >
                                        <Mail size={14} className="text-emerald-500" />
                                        {l.email}
                                    </a>
                                    <a
                                        href={`tel:${l.phone}`}
                                        className="inline-flex items-center gap-2 hover:text-emerald-600 transition-colors"
                                    >
                                        <Phone size={14} className="text-emerald-500" />
                                        {l.phone}
                                    </a>
                                    {l.amount && (
                                        <span className="inline-flex items-center gap-2">
                                            <CalculatorIcon
                                                size={14}
                                                className="text-emerald-500"
                                            />
                                            Importe: {l.amount} €
                                        </span>
                                    )}
                                </div>

                                {l.message && (
                                    <p className="mt-4 text-sm text-slate-600 leading-relaxed flex gap-2">
                                        <MessageSquare
                                            size={14}
                                            className="text-slate-300 shrink-0 mt-1"
                                        />
                                        {l.message}
                                    </p>
                                )}

                                {l.simulation && (
                                    <div
                                        data-testid={`admin-lead-simulation-${i}`}
                                        className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4"
                                    >
                                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-700">
                                            Simulación
                                        </p>
                                        <p className="mt-1.5 text-[13px] text-slate-600 whitespace-pre-line leading-relaxed">
                                            {l.simulation}
                                        </p>
                                    </div>
                                )}
                            </motion.article>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
